import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Plus, Search, Receipt, X } from "lucide-react";

/**
 * Phase 7A + Phase 7A.1 — Admin Expenses workspace.
 *
 * Add Expense UX:
 *   • Search / select existing Expense Type OR + Create Expense Type
 *   • Selecting a type prefills category / vendor / default amount
 *   • Amount always editable per transaction
 *   • One-Time / Other Expense retained
 */
const PAY_METHODS = ["cash", "card", "zelle", "cash_app", "wise", "other"];
const PAY_LABELS = { cash: "Cash", card: "Card", zelle: "Zelle",
                     cash_app: "Cash App", wise: "WISE", other: "Other" };
const STATUS_TABS = ["all", "completed", "cancelled"];
const DEFAULT_CATS = [
  "shipping", "event_fee", "supplies", "advertising",
  "merchant_fees", "transportation", "packaging",
  "printing", "software", "other",
];

function money(n) { return `$${(Number(n) || 0).toFixed(2)}`; }
function todayISO() { return new Date().toISOString().slice(0, 10); }

export default function AdminExpenses() {
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [cats, setCats] = useState(DEFAULT_CATS);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (tab !== "all") params.status = tab;
      if (q) params.q = q;
      if (category) params.category = category;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const [l, t] = await Promise.all([
        api.get("/admin/expenses", { params }),
        api.get("/admin/expenses/totals",
                  { params: { date_from: dateFrom || undefined,
                              date_to: dateTo || undefined } }),
      ]);
      setRows(l.data.expenses || []);
      setTotals(t.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed.");
    } finally { setLoading(false); }
  }

  useEffect(() => {
    api.get("/admin/expenses/categories")
        .then((r) => setCats(r.data.categories || DEFAULT_CATS))
        .catch(() => {});
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  async function cancelRow(id) {
    if (!window.confirm("Cancel this expense?")) return;
    try {
      await api.post(`/admin/expenses/${id}/cancel`);
      toast.success("Expense cancelled.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed.");
    }
  }

  return (
    <div className="min-h-screen bg-[var(--paper,_#fafaf7)]" data-testid="admin-expenses">
      <div className="max-w-5xl mx-auto p-3 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <button onClick={() => (window.location.href = "/admin")}
                    className="text-[10px] uppercase text-[var(--smoke)] flex items-center gap-1">
              <ArrowLeft size={12}/> Admin
            </button>
            <div className="text-2xl font-black flex items-center gap-2">
              <Receipt size={22}/> Expenses
            </div>
          </div>
          <button onClick={() => setShowAdd(true)}
                  data-testid="expense-add-btn"
                  className="bg-[var(--panini-red,_#dc2626)] text-white text-sm font-bold rounded-2xl px-3 py-2 flex items-center gap-1">
            <Plus size={14}/> Add Expense
          </button>
        </div>

        {totals && (
          <section className="border-2 border-[var(--ink)] rounded-2xl p-3 bg-white"
                    data-testid="expense-totals">
            <div className="text-[10px] uppercase text-[var(--smoke)] mb-1">Totals (completed)</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
              <Stat label="Total" value={money(totals.totals?.total)} testid="etotal-total"/>
              <Stat label="Count" value={totals.totals?.count || 0} testid="etotal-count"/>
              <Stat label="Tax total" value={money(totals.totals?.tax_total)}/>
            </div>
            {totals.by_category?.length > 0 && (
              <div className="pt-2 text-xs">
                <div className="text-[10px] uppercase text-[var(--smoke)] mb-0.5">By category</div>
                {totals.by_category.slice(0, 8).map((c) => (
                  <div key={c.category} className="flex justify-between">
                    <span>{c.category}</span>
                    <span className="font-mono">{money(c.total)} · {c.count}</span>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        <div className="flex gap-1 overflow-x-auto" data-testid="expense-status-tabs">
          {STATUS_TABS.map((s) => (
            <button key={s}
                    data-testid={`expense-tab-${s}`}
                    onClick={() => setTab(s)}
                    className={`text-xs px-3 py-1 rounded-full border-2 ${
                      s === tab
                        ? "bg-[var(--ink)] text-white border-[var(--ink)]"
                        : "border-[var(--ink)]"
                    }`}>
              {s}
            </button>
          ))}
        </div>

        <div className="flex gap-2 flex-wrap">
          <input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Payee · EX # · notes"
                 data-testid="expense-search-input"
                 onKeyDown={(e) => e.key === "Enter" && load()}
                 className="flex-1 min-w-[160px] border rounded-xl px-3 py-2 text-sm"/>
          <select value={category} onChange={(e) => setCategory(e.target.value)}
                  data-testid="expense-category-filter"
                  className="border rounded-xl px-2 py-2 text-sm">
            <option value="">All categories</option>
            {cats.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input type="date" value={dateFrom}
                 onChange={(e) => setDateFrom(e.target.value)}
                 data-testid="expense-date-from"
                 className="border rounded-xl px-2 py-2 text-sm"/>
          <input type="date" value={dateTo}
                 onChange={(e) => setDateTo(e.target.value)}
                 data-testid="expense-date-to"
                 className="border rounded-xl px-2 py-2 text-sm"/>
          <button onClick={load} data-testid="expense-search-btn"
                  className="bg-[var(--ink)] text-white rounded-xl px-3 text-sm font-semibold">
            <Search size={14}/>
          </button>
        </div>

        <div className="space-y-2" data-testid="expense-rows">
          {loading && <div className="text-sm text-[var(--smoke)]">Loading…</div>}
          {!loading && rows.length === 0 && (
            <div className="text-sm text-[var(--smoke)]" data-testid="expenses-empty">
              No expenses.
            </div>
          )}
          {rows.map((e) => (
            <div key={e.id} data-testid={`expense-row-${e.id}`}
                  className="border-2 border-[var(--ink)] rounded-2xl p-3 bg-white text-sm space-y-1">
              <div className="flex justify-between items-start gap-2">
                <div className="min-w-0">
                  <div className="font-bold truncate">{e.category}</div>
                  <div className="text-[11px] text-[var(--smoke)] font-mono">
                    {e.public_expense_id} · {e.expense_date} · {PAY_LABELS[e.payment_method] || e.payment_method}
                    {e.vendor_payee && ` · ${e.vendor_payee}`}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-black">{money(e.amount)}</div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                    e.status === "completed" ? "bg-green-200" : "bg-gray-300"
                  }`}>{e.status}</span>
                </div>
              </div>
              {e.notes && <div className="text-xs">{e.notes}</div>}
              {e.status === "completed" && (
                <div className="flex gap-1 pt-1">
                  <button onClick={() => setEditing(e)}
                          data-testid={`expense-edit-${e.id}`}
                          className="text-[11px] border-2 border-[var(--ink)] rounded-full px-2 py-0.5">
                    Edit
                  </button>
                  <button onClick={() => cancelRow(e.id)}
                          data-testid={`expense-cancel-${e.id}`}
                          className="text-[11px] border-2 border-red-500 text-red-700 rounded-full px-2 py-0.5">
                    Cancel
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {showAdd && <ExpenseEditor cats={cats}
                                    onClose={() => setShowAdd(false)}
                                    onSaved={() => { setShowAdd(false); load(); }}/>}
      {editing && <ExpenseEditor existing={editing} cats={cats}
                                    onClose={() => setEditing(null)}
                                    onSaved={() => { setEditing(null); load(); }}/>}
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="border rounded-xl px-2 py-1" data-testid={testid}>
      <div className="text-[10px] uppercase text-[var(--smoke)]">{label}</div>
      <div className="font-bold text-sm">{value}</div>
    </div>
  );
}

function ExpenseTypePicker({ onPick, onOneTime, cats }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    const h = setTimeout(async () => {
      try {
        const r = await api.get("/admin/expense-items",
                                    { params: { q: q || undefined, limit: 15,
                                                active: true } });
        setItems(r.data?.expense_items || []);
      } catch { /* ignore */ }
    }, 180);
    return () => clearTimeout(h);
  }, [q]);

  return (
    <div className="border rounded-xl p-2 space-y-2" data-testid="expense-type-picker">
      <div className="text-[10px] uppercase text-[var(--smoke)]">Search Expense Type</div>
      <input value={q} onChange={(e) => setQ(e.target.value)}
             placeholder="e.g. Shopify Subscription, Haulover Park Event Fee…"
             data-testid="expense-type-search"
             autoFocus
             className="w-full border rounded-lg px-2 py-1.5 text-sm"/>
      <div className="space-y-1 max-h-56 overflow-auto">
        {items.map((it) => (
          <button key={it.id} onClick={() => onPick(it)}
                  data-testid={`pick-expense-item-${it.id}`}
                  className="w-full text-left text-xs border rounded-lg px-2 py-1 hover:bg-gray-50">
            <b>{it.name}</b>
            {it.default_category && <span className="text-[var(--smoke)]"> · {it.default_category}</span>}
            {it.default_amount != null &&
              <span className="text-[var(--smoke)]"> · {money(it.default_amount)}</span>}
          </button>
        ))}
        {items.length === 0 && (
          <div className="text-xs text-[var(--smoke)] italic">No matches.</div>
        )}
      </div>
      <div className="flex gap-1">
        <button onClick={() => setShowCreate(true)}
                data-testid="expense-type-create-btn"
                className="flex-1 text-xs border-2 border-[var(--ink)] rounded-full px-2 py-1">
          + Create Expense Type
        </button>
        <button onClick={onOneTime}
                data-testid="expense-type-onetime"
                className="flex-1 text-xs border-2 border-dashed border-[var(--ink)] rounded-full px-2 py-1">
          + One-Time / Other
        </button>
      </div>
      {showCreate && <CreateExpenseItemModal prefillName={q} cats={cats}
                                                onClose={() => setShowCreate(false)}
                                                onCreated={(it) => { setShowCreate(false); onPick(it); }}/>}
    </div>
  );
}

function CreateExpenseItemModal({ prefillName, cats, onClose, onCreated }) {
  const [f, setF] = useState({ name: prefillName || "", default_category: "",
                                  default_vendor_payee: "", default_amount: "" });
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!f.name.trim()) return toast.error("Name required.");
    const body = { name: f.name.trim() };
    if (f.default_category) body.default_category = f.default_category;
    if (f.default_vendor_payee) body.default_vendor_payee = f.default_vendor_payee;
    if (f.default_amount) body.default_amount = Number(f.default_amount);
    setBusy(true);
    try {
      const r = await api.post("/admin/expense-items", body);
      toast.success("Expense type created.");
      onCreated(r.data.expense_item);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed.");
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-[110] bg-black/50 flex items-center justify-center p-3">
      <div className="bg-white rounded-2xl border-2 border-[var(--ink)] w-full max-w-sm p-3 space-y-2"
            data-testid="create-expense-item-modal">
        <div className="flex justify-between">
          <div className="font-bold">Create Expense Type</div>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <input value={f.name} data-testid="cei-name"
               onChange={(e) => setF({ ...f, name: e.target.value })}
               placeholder="Name * (e.g. Shopify Subscription)"
               className="w-full border rounded-xl px-3 py-2 text-sm"/>
        <select value={f.default_category}
                onChange={(e) => setF({ ...f, default_category: e.target.value })}
                data-testid="cei-category"
                className="w-full border rounded-xl px-2 py-2 text-sm">
          <option value="">Default category (optional)</option>
          {cats.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <input value={f.default_vendor_payee}
               onChange={(e) => setF({ ...f, default_vendor_payee: e.target.value })}
               placeholder="Default vendor / payee (optional)"
               className="w-full border rounded-xl px-3 py-2 text-sm"/>
        <input type="number" step="0.01" value={f.default_amount}
               onChange={(e) => setF({ ...f, default_amount: e.target.value })}
               placeholder="Default amount (optional)"
               className="w-full border rounded-xl px-3 py-2 text-sm"/>
        <button onClick={save} disabled={busy} data-testid="cei-save-btn"
                className={`w-full rounded-2xl py-2 font-bold text-white ${
                  busy ? "bg-gray-400" : "bg-[var(--ink)]"
                }`}>
          {busy ? "Saving…" : "Create Expense Type"}
        </button>
      </div>
    </div>
  );
}

function ExpenseEditor({ existing, cats, onClose, onSaved }) {
  const isEdit = !!existing;
  const [pickerMode, setPickerMode] = useState(!isEdit);
  const [expenseItem, setExpenseItem] = useState(null);
  const [f, setF] = useState(() => existing ? {
    expense_date:  existing.expense_date || todayISO(),
    category:      existing.category || (cats[0] || "other"),
    amount:        existing.amount ?? 0,
    tax:           existing.tax ?? "",
    payment_method: existing.payment_method || "card",
    vendor_payee:  existing.vendor_payee || "",
    notes:         existing.notes || "",
  } : {
    expense_date: todayISO(),
    category: cats[0] || "other",
    amount: "", tax: "", payment_method: "card",
    vendor_payee: "", notes: "",
  });
  const [busy, setBusy] = useState(false);

  function applyExpenseItem(item) {
    setExpenseItem(item);
    setPickerMode(false);
    setF((prev) => ({ ...prev,
      category:     item.default_category || prev.category,
      vendor_payee: item.default_vendor_payee || prev.vendor_payee,
      amount:       item.default_amount != null ? String(item.default_amount) : prev.amount,
      notes:        prev.notes || (item.name ? `[${item.name}]` : prev.notes),
    }));
  }

  async function save() {
    if (!f.category) return toast.error("Category required.");
    if (f.amount === "" || Number.isNaN(Number(f.amount)))
      return toast.error("Amount required.");
    const body = {
      expense_date: f.expense_date,
      category: f.category,
      amount: Number(f.amount),
      tax: f.tax === "" ? null : Number(f.tax),
      payment_method: f.payment_method,
      vendor_payee: f.vendor_payee || null,
      notes: f.notes || null,
    };
    if (isEdit && body.tax === null) delete body.tax;
    setBusy(true);
    try {
      if (isEdit) await api.patch(`/admin/expenses/${existing.id}`, body);
      else await api.post("/admin/expenses", body);
      toast.success(isEdit ? "Expense updated." : "Expense added.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-[100] bg-black/40 flex items-end sm:items-center justify-center sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl border-2 border-[var(--ink)] w-full max-w-md p-4 space-y-2 max-h-[92vh] overflow-auto"
           data-testid="expense-editor-modal">
        <div className="flex items-center justify-between">
          <div className="font-bold">{isEdit ? "Edit Expense" : "Add Expense"}</div>
          <button onClick={onClose}><X size={20}/></button>
        </div>

        {pickerMode ? (
          <ExpenseTypePicker cats={cats}
                                onPick={applyExpenseItem}
                                onOneTime={() => setPickerMode(false)}/>
        ) : (
          <>
            {expenseItem && (
              <div className="text-[10px] flex justify-between border rounded-lg p-1 bg-gray-50">
                <span>Type: <b>{expenseItem.name}</b></span>
                <button onClick={() => { setPickerMode(true); setExpenseItem(null); }}
                        className="underline">change</button>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="text-[10px] uppercase text-[var(--smoke)]">Date</div>
                <input type="date" value={f.expense_date}
                       data-testid="expense-date"
                       onChange={(e) => setF({ ...f, expense_date: e.target.value })}
                       className="w-full border rounded-xl px-2 py-1.5 text-sm"/>
              </div>
              <div>
                <div className="text-[10px] uppercase text-[var(--smoke)]">Category</div>
                <select value={f.category}
                        data-testid="expense-category"
                        onChange={(e) => setF({ ...f, category: e.target.value })}
                        className="w-full border rounded-xl px-2 py-1.5 text-sm">
                  {cats.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="text-[10px] uppercase text-[var(--smoke)]">Amount *</div>
                <input type="number" step="0.01" value={f.amount}
                       data-testid="expense-amount"
                       onChange={(e) => setF({ ...f, amount: e.target.value })}
                       className="w-full border rounded-xl px-3 py-2 text-sm"/>
              </div>
              <div>
                <div className="text-[10px] uppercase text-[var(--smoke)]">Tax (opt)</div>
                <input type="number" step="0.01" value={f.tax}
                       onChange={(e) => setF({ ...f, tax: e.target.value })}
                       className="w-full border rounded-xl px-3 py-2 text-sm"/>
              </div>
            </div>

            <div>
              <div className="text-[10px] uppercase text-[var(--smoke)]">Payment</div>
              <select value={f.payment_method}
                      data-testid="expense-payment-method"
                      onChange={(e) => setF({ ...f, payment_method: e.target.value })}
                      className="w-full border rounded-xl px-2 py-1.5 text-sm">
                {PAY_METHODS.map((p) => <option key={p} value={p}>{PAY_LABELS[p]}</option>)}
              </select>
            </div>

            <input value={f.vendor_payee}
                   data-testid="expense-vendor-payee"
                   onChange={(e) => setF({ ...f, vendor_payee: e.target.value })}
                   placeholder="Vendor / payee (optional)"
                   className="w-full border rounded-xl px-3 py-2 text-sm"/>
            <textarea value={f.notes}
                      onChange={(e) => setF({ ...f, notes: e.target.value })}
                      placeholder="Notes (optional)"
                      className="w-full border rounded-xl px-3 py-2 text-sm h-14"/>

            <button onClick={save} disabled={busy}
                    data-testid="expense-save-btn"
                    className={`w-full rounded-2xl py-2 font-bold text-white ${
                      busy ? "bg-gray-400" : "bg-[var(--panini-red,_#dc2626)]"
                    }`}>
              {busy ? "Saving…" : isEdit ? "Save Changes" : "Add Expense"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
